import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Button,
} from "@mui/material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import MaterialTable from "../../components/Table/MaterialTable";
import HotelForm from "./HotelForm";

const Hotels = () => {
  const [tableData, setTableData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const columns = [
    { title: "ID", field: "id" },
    { title: "Hotel ID", field: "hotel_id" },
    { title: "Org ID", field: "org_id" },
    { title: "Brand ID", field: "brand_id" },
    { title: "Name", field: "name" },
    { title: "Local Name", field: "local_name" },
    { title: "Property Code", field: "property_code" },
    {
      title: "Description",
      field: "description",
      render: (rowData) => (
        <div
          style={{
            maxWidth: 200,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {rowData.description}
        </div>
      ),
    },
    { title: "Short Description", field: "short_description" },
    { title: "Star Rating", field: "star_rating" },
    { title: "Property Type", field: "property_type" },
    { title: "Address Line 1", field: "address_line1" },
    { title: "Address Line 2", field: "address_line2" },
    { title: "City", field: "city" },
    { title: "State", field: "state" },
    { title: "Country", field: "country" },
    { title: "Postal Code", field: "postal_code" },
    { title: "Latitude", field: "latitude" },
    { title: "Longitude", field: "longitude" },
    { title: "Timezone", field: "timezone" },
    { title: "Phone", field: "phone" },
    { title: "Email", field: "email" },
    { title: "Website URL", field: "website_url" },
    { title: "Check-in Time", field: "check_in_time" },
    { title: "Check-out Time", field: "check_out_time" },
    { title: "Min Advance Booking", field: "min_advance_booking" },
    { title: "Max Advance Booking", field: "max_advance_booking" },
    { title: "Cancellation Policy (hrs)", field: "cancellation_policy_hours" },
    { title: "Default Currency", field: "default_currency" },
    { title: "Tax Rate", field: "tax_rate" },
    { title: "Resort Fee", field: "resort_fee" },
    {
      title: "Status",
      field: "is_active",
      render: (rowData) => (
        <Chip
          label={rowData.is_active ? "Active" : "Inactive"}
          color={rowData.is_active ? "success" : "error"}
          size="small"
        />
      ),
    },
  ];

  const fetchData = async () => {
    try {
      const response = await fetch("https://api.esthell.com/Hotel");
      if (!response.ok) throw new Error("Failed to fetch data");
      const responseData = await response.json();

      if (responseData.success && Array.isArray(responseData.data)) {
        setTableData(responseData.data);
      } else {
        setTableData([]);
        setError("Invalid response format.");
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleExportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(tableData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Hotels");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(data, `Hotels_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const handleFormSubmit = async (formData) => {
    try {
      // Common data transformation
      const commonData = {
        ...formData,
        default_currency: "INR",
        timezone: formData.timezone || "Asia/Kolkata",
        org_id: formData.org_id ? parseInt(formData.org_id) : null,
        brand_id: formData.brand_id ? parseInt(formData.brand_id) : null,
        star_rating: formData.star_rating
          ? parseInt(formData.star_rating)
          : null,
        min_advance_booking: parseInt(formData.min_advance_booking),
        max_advance_booking: parseInt(formData.max_advance_booking),
        cancellation_policy_hours: parseInt(formData.cancellation_policy_hours),
        tax_rate: parseFloat(formData.tax_rate),
        resort_fee: parseFloat(formData.resort_fee),
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      };

      if (editData?.id) {
        // Update existing hotel
        const payload = {
          data: {
            ...commonData,
            hotel_id: parseInt(editData.hotel_id),
          },
        };

        const response = await fetch(
          `https://api.esthell.com/Hotel/${editData.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to update hotel");
        }
        setNotification({
          open: true,
          message: "Hotel updated successfully!",
          type: "success",
        });
      } else {
        // Generate hotel_id automatically
        const numberingPayload = {
          warehouse_id: "WH001",
          numbering_type: "Ho",
          gen_year: new Date().getFullYear(),
        };

        const numberingResponse = await fetch(
          "https://api.esthell.com/location_numbertype_mst",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(numberingPayload),
          },
        );

        if (!numberingResponse.ok) {
          throw new Error("Failed to generate hotel ID");
        }

        const numberingData = await numberingResponse.json();
        // Increment the last_gen_number by 1 for the new hotel_id
        const nextHotelId = parseInt(numberingData[0].last_gen_number) + 1;

        // Create hotel with generated hotel_id
        const payload = {
          data: {
            ...formData,
            hotel_id: nextHotelId,
            org_id: formData.org_id ? parseInt(formData.org_id) : null,
            brand_id: formData.brand_id ? parseInt(formData.brand_id) : null,
            star_rating: formData.star_rating
              ? parseInt(formData.star_rating)
              : null,
            min_advance_booking: parseInt(formData.min_advance_booking),
            max_advance_booking: parseInt(formData.max_advance_booking),
            cancellation_policy_hours: parseInt(
              formData.cancellation_policy_hours,
            ),
            tax_rate: parseFloat(formData.tax_rate),
            resort_fee: parseFloat(formData.resort_fee),
            latitude: formData.latitude ? parseFloat(formData.latitude) : null,
            longitude: formData.longitude
              ? parseFloat(formData.longitude)
              : null,
          },
        };

        const response = await fetch("https://api.esthell.com/create/Hotel", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to create hotel");
        }
        setNotification({
          open: true,
          message: "Hotel created successfully!",
          type: "success",
        });
      }

      setShowForm(false);
      setEditData(null);
      fetchData();
    } catch (err) {
      setError(err.message);
      setNotification({ open: true, message: err.message, type: "error" });
      console.error("Error:", err);
    }
  };

  const handleEdit = (row) => {
    setEditData(row);
    setShowForm(true);
  };

  const handleDelete = async (row) => {
    try {
      const response = await fetch(`https://api.esthell.com/Hotel/${row.id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete hotel");
      }

      fetchData();
      setNotification({
        open: true,
        message: "Hotel deleted successfully!",
        type: "success",
      });
    } catch (err) {
      setError(err.message);
      setNotification({ open: true, message: err.message, type: "error" });
      console.error("Error:", err);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4" sx={{ color: "#1a237e", fontWeight: "bold" }}>
          Hotels
        </Typography>
        <Button variant="outlined" onClick={handleExportToExcel}>
          Export to Excel
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Box sx={{ mt: "1cm", width: "100%" }}>
          <MaterialTable
            columns={columns}
            data={tableData}
            selection={false}
            actions={false}
            onEdit={handleEdit}
            onDelete={handleDelete}
            toolbar={{ create: true, onCreateClick: () => setShowForm(true) }}
            sx={{
              "& .MuiPaper-root": { width: "100%", overflow: "hidden" },
              "& .MuiTableContainer-root": {
                maxHeight: "calc(100vh - 250px)",
                overflowX: "auto",
                overflowY: "auto",
              },
            }}
          />
        </Box>
      )}

      <Dialog
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setEditData(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: "90vh", overflowY: "auto" } }}
      >
        <Box sx={{ p: 3 }}>
          <HotelForm
            handleClose={() => {
              setShowForm(false);
              setEditData(null);
            }}
            handleSubmit={handleFormSubmit}
            initialData={editData}
          />
        </Box>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.type}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Hotels;
